import { useQuery } from '@apollo/client';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { IComboBoxOption, IDropdownOption } from '@fluentui/react';
import {
  Field,
  Radio,
  RadioGroup,
  FluentProvider,
  webLightTheme,
  Textarea,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import React, { useState } from 'react';
import { Controller, FieldValues, UseFormMethods, useWatch } from 'react-hook-form';
import { GetControllersAreFTECheck } from '../../../../utils/statApi/CompaniesApi';
import GET_ENTITY_STATUSES_QUERY, {
  GET_LIFECYCLE_STATUSES_QUERY,
} from '../../../../utils/statApi/EntityStatusApi';
import {
  ENTITY_STATUS_ACTIVE,
  ENTITY_STATUS_TERMINATED,
} from '../../../../app/common/constants/EntityStatusConstants';
import GET_ENTITY_TYPES_QUERY from '../../../../utils/statApi/EntityTypeApi';
import { yesOption, noOption } from '../../../../utils/types/CommonDropdownOptions';
import {
  ICompanyBusinessNature,
  ILifecycleStatus,
} from '../../../../utils/types/ICompanyBusinessNature';
import IDropdownReference from '../../../../utils/types/IDropdownReference';
import IFieldsetProps from '../../../../utils/types/IFieldsetProps';
import QueryBasedDropdown from '../../../common/formFields/queryBasedDropdown/QueryBasedDropdown';
import UtcDatePicker from '../../../common/utcDatePicker/UtcDatePicker';
import AcquisitionSelector from '../../../companyCreation/AcquisitionSelector';
import ReportingGroupSelector from '../../../companyCreation/ReportingGroupSelector';

interface ICompanyBusinessNatureFormProps {
  form: UseFormMethods<FieldValues>;
  data: ICompanyBusinessNature;
  companyCode: string;
}

const CompanyBusinessNatureForm = ({
  form,
  data,
  companyCode,
}: ICompanyBusinessNatureFormProps): JSX.Element => {
  const { control, errors: formErrors, setValue, watch, getValues } = form;
  const [controllerMustBeFTEMessage, setControllerMustBeFTEMessage] = useState<boolean>(
    data?.controllerMustBeFte,
  );
  const [hasEntityStatusChanged, setHasEntityStatusChanged] = useState<boolean>(false);
  const { loading: loadingFTEValidator, data: dataFTEValidator } = useQuery<{
    controllersAreFTECheck: { areControllersFTE: boolean };
  }>(GetControllersAreFTECheck, {
    fetchPolicy: 'network-only',
    variables: { companyCode },
  });

  const onAcquisitionChange = (value: IComboBoxOption): void => {
    setValue('acquisition', value);
  };

  const watchIsAcquisition = watch('isAcquisition', data?.isAcquisition);

  const watchEntityStatus = useWatch({
    control,
    name: 'entityStatus',
    defaultValue: data?.entityStatus,
  });

  const onEntityTypeChange = (value: IDropdownOption): void => {
    setValue('entityType', value);
  };

  const onLifecycleStatusChange = (value: IDropdownOption): void => {
    setValue('lifecycleStatus', value);
  };

  const onReportingGroupChange = (value: IDropdownOption): void => {
    setValue('reportingGroup', value);
  };

  const onEntityStatusChange = (value: IDropdownOption): void => {
    setValue('entityStatus', value);
    setHasEntityStatusChanged(getValues('entityStatus')?.id !== data?.entityStatus?.id);
    setValue('lifecycleStatus', null);
  };

  const isActiveOrTerminated =
    watchEntityStatus?.fieldName === ENTITY_STATUS_ACTIVE ||
    watchEntityStatus?.fieldName === ENTITY_STATUS_TERMINATED;

  const EntityTypeDropdown = (props: IFieldsetProps): JSX.Element => {
    const { value, label, required, errors: etErrors, handleChange, valueKey, outputKey } = props;
    const { loading, data: entityTypesData } = useQuery(GET_ENTITY_TYPES_QUERY);
    const { entityTypes } = !loading && entityTypesData ? entityTypesData : [];
    return (
      <QueryBasedDropdown
        value={value as IDropdownReference}
        outputKey={outputKey}
        label={label}
        required={required}
        errors={etErrors}
        dropdownOptionsData={entityTypes}
        handleChange={handleChange}
        valueKey={valueKey}
      />
    );
  };
  const EntityStatusDropdown = (props: IFieldsetProps): JSX.Element => {
    const { value, label, required, errors, handleChange, valueKey, outputKey } = props;
    const { loading, data: entityStatusData } = useQuery(GET_ENTITY_STATUSES_QUERY);
    const { entityStatuses } = !loading && entityStatusData ? entityStatusData : [];

    return (
      <QueryBasedDropdown
        value={value as IDropdownReference}
        label={label}
        required={required}
        outputKey={outputKey}
        dropdownOptionsData={entityStatuses}
        errors={errors}
        handleChange={handleChange}
        valueKey={valueKey}
      />
    );
  };

  const LifecycleStatusDropdown = (props: IFieldsetProps): JSX.Element => {
    const { value, label, errors, handleChange, valueKey, outputKey } = props;
    const { loading, data: lifecycleStatusData } = useQuery(GET_LIFECYCLE_STATUSES_QUERY);
    const { lifecycleStatuses } = !loading && lifecycleStatusData ? lifecycleStatusData : [];

    const entityStatusId = watchEntityStatus?.id;
    const updatedLifecycleStatuses = lifecycleStatuses?.map(
      (lifecycleStatus: ILifecycleStatus) => ({
        ...lifecycleStatus,
        // Changed: Check if entityStatusId is in the array of entity status IDs
        isSelectable: lifecycleStatus.entityStatusIds?.includes(entityStatusId),
      }),
    );

    return (
      <QueryBasedDropdown
        value={value as IDropdownReference}
        label={label}
        outputKey={outputKey}
        dropdownOptionsData={updatedLifecycleStatuses}
        errors={errors}
        handleChange={handleChange}
        valueKey={valueKey}
      />
    );
  };

  const validateAcquisition = (): string => {
    if (!getValues('acquisition')?.text?.trim() && watchIsAcquisition) {
      return 'Please enter or select an acquisition';
    }
    return null;
  };
  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 10 }}>
        <Controller
          name="entityType"
          label="Entity Type"
          valueKey="entityType"
          control={control}
          defaultValue={data?.entityType}
          render={(props: { value: IDropdownReference }) => {
            return (
              <EntityTypeDropdown
                label="Entity Type"
                valueKey="entityType"
                required
                errors={formErrors}
                handleChange={onEntityTypeChange}
                value={props.value}
              />
            );
          }}
          rules={{
            required: 'Please select an entity type',
          }}
        />
        <Controller
          name="isAcquisition"
          control={control}
          render={() => {
            return (
              <Field label="Is Associated With Acquisition" required>
                <RadioGroup
                  id="isAcquisition"
                  value={watchIsAcquisition ? 'true' : 'false'}
                  onChange={(e, radioData): void => {
                    setValue('isAcquisition', radioData.value === 'true');
                  }}
                >
                  <Radio value={yesOption.key} label={yesOption.text} />
                  <Radio value={noOption.key} label={noOption.text} />
                </RadioGroup>
              </Field>
            );
          }}
        />
        <Controller
          name="acquisition"
          defaultValue={data?.acquisition}
          control={control}
          render={() => {
            return (
              <AcquisitionSelector
                label="Acquisition Name"
                disabled={!watchIsAcquisition}
                onChange={onAcquisitionChange}
                required={watchIsAcquisition}
                errorMessage={watchIsAcquisition ? formErrors?.acquisition?.message : ''}
                defaultValue={data?.acquisition}
              />
            );
          }}
          rules={{
            validate: validateAcquisition,
          }}
        />

        <Controller
          name="dateOfAcquisition"
          control={control}
          disabled={!watchIsAcquisition}
          defaultValue={data?.dateOfAcquisition}
          required={watchIsAcquisition}
          rules={
            watchIsAcquisition
              ? {
                  required: 'Please select an acquisition date',
                }
              : {}
          }
          render={({ value }): React.ReactElement => (
            <div role="form">
              <UtcDatePicker
                label="Date of Acquisition"
                isRequired={watchIsAcquisition}
                disabled={!watchIsAcquisition}
                allowTextInput
                disableAutoFocus={false}
                value={value}
                textField={{
                  name: 'dateOfAcquisition',
                  errorMessage: formErrors?.dateOfAcquisition?.message,
                }}
                onSelectDate={(utcDateString: string): void => {
                  setValue('dateOfAcquisition', utcDateString);
                }}
              />
            </div>
          )}
        />

        <Controller
          name="entityStatus"
          label="Legal Status"
          valueKey="entityStatus"
          control={control}
          defaultValue={data?.entityStatus}
          render={(props: { value: IDropdownReference }) => {
            return (
              <EntityStatusDropdown
                label="Legal Status"
                valueKey="entityStatus"
                required
                errors={formErrors}
                handleChange={onEntityStatusChange}
                value={props.value}
              />
            );
          }}
          rules={{
            required: 'Please select a legal status',
          }}
        />
        <Controller
          name="lifecycleStatus"
          label="Lifecycle Status"
          valueKey="lifecycleStatus"
          control={control}
          defaultValue={data?.lifecycleStatus}
          render={(props: { value: IDropdownReference }) => {
            return (
              <LifecycleStatusDropdown
                label="Lifecycle Status"
                valueKey="lifecycleStatus"
                required={isActiveOrTerminated}
                errors={formErrors}
                handleChange={onLifecycleStatusChange}
                value={props.value}
              />
            );
          }}
          rules={
            isActiveOrTerminated
              ? {
                  required: 'Please select a Lifecycle Status',
                }
              : {}
          }
        />

        <Controller
          name="effectiveDateOfStatusChange"
          control={control}
          defaultValue={data?.effectiveDateOfStatusChange}
          render={({ value }): React.ReactElement => (
            <div role="form">
              <UtcDatePicker
                label="Effective Date of Status Change"
                isRequired={hasEntityStatusChanged}
                allowTextInput
                disableAutoFocus={false}
                value={value}
                textField={{
                  name: 'effectiveDateOfStatusChange',
                  errorMessage: formErrors?.effectiveDateOfStatusChange?.message,
                }}
                onSelectDate={(utcDateString: string): void => {
                  setValue('effectiveDateOfStatusChange', utcDateString);
                }}
              />
            </div>
          )}
          rules={
            hasEntityStatusChanged
              ? {
                  required: 'Please select an Effective Date of Status Change',
                }
              : {}
          }
        />

        <Controller
          name="businessNatureOfEntity"
          control={control}
          defaultValue={data?.businessNatureOfEntity}
          render={({ onChange, name, value }): React.ReactElement => (
            <Field
              label="Business Nature of Entity"
              required
              validationMessage={formErrors?.businessNatureOfEntity?.message}
            >
              <Textarea
                id="businessNatureOfEntity"
                name={name}
                value={value}
                onChange={(e, textData): void => {
                  onChange(textData.value);
                }}
                resize="vertical"
              />
            </Field>
          )}
          rules={{
            required: 'Business Nature of Entity is required',
          }}
        />
        {!loadingFTEValidator &&
          !dataFTEValidator?.controllersAreFTECheck.areControllersFTE &&
          controllerMustBeFTEMessage && (
            <MessageBar intent="warning" layout="multiline">
              <MessageBarBody>
                One or more controllers are not FTE. You may still save this change.
              </MessageBarBody>
            </MessageBar>
          )}
        <Controller
          name="controllerMustBeFte"
          defaultValue={data?.controllerMustBeFte?.toString().toLowerCase()}
          control={control}
          render={({ value }): React.ReactElement => {
            return (
              <Field label="Controller Must Be FTE?" required>
                <RadioGroup
                  id="controllerMustBeFte"
                  name="controllerMustBeFte"
                  value={value?.toString().toLowerCase()}
                  onChange={(e, radioData): void => {
                    const enabled = radioData.value === 'true';
                    setValue('controllerMustBeFte', enabled);
                    setControllerMustBeFTEMessage(enabled);
                  }}
                >
                  <Radio value={yesOption.key} label={yesOption.text} />
                  <Radio value={noOption.key} label={noOption.text} />
                </RadioGroup>
              </Field>
            );
          }}
        />
        <Controller
          name="reportingGroup"
          defaultValue={data?.reportingGroup}
          control={control}
          render={() => {
            return (
              <ReportingGroupSelector
                label="Reporting Group"
                disabled={false}
                onChange={onReportingGroupChange}
                required
                errorMessage={formErrors?.reportingGroup?.message}
                defaultValue={data?.reportingGroup}
              />
            );
          }}
          rules={{
            required: 'Please select a Reporting Group',
          }}
        />
      </Stack>
    </FluentProvider>
  );
};

export default CompanyBusinessNatureForm;
